import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { legalEntityClasses } from '../config/calendarTheme';
import type { CalendarEvent } from '../../types/calendar.types';

/**
 * Event block node data structure for Interactive Agenda
 */
export interface EventBlockData {
  eventId: string;
  title: string;
  startTime: string;
  endTime: string;
  eventType: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  attendees: string[];
  location?: string;
}

/**
 * Serialized event block node for JSON storage
 */
export interface SerializedEventBlockNode extends Spread<
  {
    eventData: EventBlockData;
    type: 'event-block';
    version: 1;
  },
  SerializedLexicalNode
> {}

/**
 * Custom Lexical node for embedding calendar events in Interactive Agenda
 * These are immutable blocks that represent scheduled events
 */
export class EventBlockNode extends DecoratorNode<React.JSX.Element> {
  __eventData: EventBlockData;

  static getType(): string {
    return 'event-block';
  }

  static clone(node: EventBlockNode): EventBlockNode {
    return new EventBlockNode(node.__eventData, node.__key);
  }

  constructor(eventData: EventBlockData, key?: NodeKey) {
    super(key);
    this.__eventData = eventData;
  }

  exportJSON(): SerializedEventBlockNode {
    return {
      eventData: this.__eventData,
      type: 'event-block',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedEventBlockNode): EventBlockNode {
    const { eventData } = serializedNode;
    return $createEventBlockNode(eventData);
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = legalEntityClasses.eventBlock;
    return div;
  }

  updateDOM(): false {
    return false; // Immutable - events don't change within the agenda
  }

  decorate(): React.JSX.Element {
    return (
      <EventBlockComponent
        eventData={this.__eventData}
        nodeKey={this.getKey()}
      />
    );
  }

  getTextContent(): string {
    return `${this.__eventData.startTime} - ${this.__eventData.endTime}: ${this.__eventData.title}`;
  }

  isImmutable(): boolean {
    return true; // Events are read-only in the agenda
  }

  isKeyboardSelectable(): boolean {
    return false; // Don't allow keyboard selection to maintain flow
  }

  // Prevent line breaks within event blocks
  isInline(): boolean {
    return false;
  }

  // Event blocks are always block-level elements
  isBlock(): boolean {
    return true;
  }
}

/**
 * React component for rendering event blocks in Interactive Agenda
 */
interface EventBlockComponentProps {
  eventData: EventBlockData;
  nodeKey: NodeKey;
}

function EventBlockComponent({ eventData }: EventBlockComponentProps): React.JSX.Element {
  const handleClick = () => {
    // Navigate to event detail or open event modal
    console.log('Event clicked:', eventData.eventId);
    // This will be integrated with the calendar event system
  };

  const getPriorityColor = () => {
    switch (eventData.priority) {
      case 'urgent':
        return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'high':
        return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'medium':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'border-lex-accent bg-lex-accent-soft';
    }
  };

  const getEventTypeIcon = () => {
    switch (eventData.eventType) {
      case 'court':
        return '⚖️';
      case 'meeting':
        return '👥';
      case 'deadline':
        return '📅';
      case 'call':
        return '📞';
      default:
        return '📋';
    }
  };

  return (
    <div
      className={`${legalEntityClasses.eventBlock} ${getPriorityColor()} cursor-pointer hover:shadow-md transition-shadow`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-lg" aria-hidden="true">
            {getEventTypeIcon()}
          </span>
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {eventData.title}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {eventData.startTime} - {eventData.endTime}
            </div>
            {eventData.location && (
              <div className="text-sm text-gray-500 dark:text-gray-500">
                📍 {eventData.location}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className={`text-xs px-2 py-1 rounded-full ${
            eventData.priority === 'urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' :
            eventData.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' :
            eventData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200' :
            'bg-lex-accent-soft text-lex-accent'
          }`}>
            {eventData.priority}
          </div>
          {eventData.attendees.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {eventData.attendees.length} attendee{eventData.attendees.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Factory function for creating event block nodes
 */
export function $createEventBlockNode(eventData: EventBlockData): EventBlockNode {
  return new EventBlockNode(eventData);
}

/**
 * Helper function to create event block from calendar event
 */
export function $createEventBlockFromCalendarEvent(event: CalendarEvent): EventBlockNode {
  const eventData: EventBlockData = {
    eventId: event.id,
    title: event.title,
    startTime: event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    endTime: event.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    eventType: event.eventTypeId || 'meeting',
    priority: event.priority || 'medium',
    attendees: event.attendees?.map(a => a.name) || [],
    location: event.location,
  };
  
  return $createEventBlockNode(eventData);
}

/**
 * Type guard for event block nodes
 */
export function $isEventBlockNode(node: LexicalNode | null | undefined): node is EventBlockNode {
  return node instanceof EventBlockNode;
} 